import { useState, type FormEvent } from "react";
import { Plus } from "lucide-react";
import {
  actions,
  listPendingApprovalReassignments,
  listTeam,
} from "@/lib/api";
import { displayDate, titleCase } from "@/lib/utils";
import type {
  PendingApprovalReassignment,
  TeamMember,
} from "@/lib/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useData, matches, type ViewProps, type DialogProps } from "@/lib/app-view";
import { PageHeader } from "@/components/page-header";
import { Empty, Loading, ErrorState } from "@/components/empty-state";
import { Table } from "@/components/data-table";
import { FieldError } from "@/components/field-error";

const roles = [
  ["requester", "Requester"],
  ["approver", "Approver"],
  ["procurement", "Procurement"],
  ["receiver", "Receiver"],
  ["finance", "Finance"],
  ["auditor", "Auditor"],
  ["tenant_admin", "Organisation administrator"],
] as const;

type MemberAction =
  | {
      kind: "role" | "suspend" | "reactivate" | "revoke" | "resend" | "cancel";
      member: TeamMember;
    }
  | { kind: "reassign_pending"; pending: PendingApprovalReassignment };

export function Team({
  organisation,
  refresh,
  query,
  can,
  changed,
  currentUserId,
}: ViewProps & { currentUserId: string }) {
  const { data, error, loading } = useData(
    () =>
      can("manage")
        ? Promise.all([
            listTeam(organisation.id),
            listPendingApprovalReassignments(organisation.id),
          ]).then(([members, pending]) => ({ members, pending }))
        : Promise.resolve({ members: [], pending: [] }),
    [organisation.id, refresh],
  );
  const [inviting, setInviting] = useState(false);
  const [memberAction, setMemberAction] = useState<MemberAction>();
  if (!can("manage"))
    return (
      <>
        <PageHeader
          eyebrow="ACCESS CONTROL"
          title="Team & access"
          description="Your role does not permit user administration."
        />
        <Empty
          title="Read-only access"
          description="An organisation administrator or Cyclotron platform owner manages membership."
        />
      </>
    );
  if (loading) return <Loading />;
  if (error || !data) return <ErrorState message={error} />;
  const rows = data.members.filter((row) =>
    matches(
      query,
      row.profiles?.full_name,
      row.profiles?.email,
      row.role,
      row.status,
      row.invitation_state,
    ),
  );
  return (
    <>
      <PageHeader
        eyebrow="ACCESS CONTROL"
        title="Team & access"
        description="Give each person the minimum role needed for their work. Every access change requires a reason and is audited."
      >
        <Button className="min-h-11" onClick={() => setInviting(true)}>
          <Plus className="size-4" />
          Invite user
        </Button>
      </PageHeader>
      {data.pending.length > 0 && (
        <section className="mb-6" aria-labelledby="approval-reassignment-title">
          <h2
            id="approval-reassignment-title"
            className="mb-3 text-lg font-semibold text-[var(--text-primary)]"
          >
            Approval reassignment required
          </h2>
          <p className="mb-4 text-sm text-[var(--text-secondary)]">
            These submitted requisitions are paused until an active approver is assigned.
          </p>
          <div className="hidden md:block">
            <Table heads={["Former approver", "Email", "Requests", "Action"]}>
              {data.pending.map((pending) => (
                <tr key={pending.former_user_id}>
                  <td><strong>{pending.full_name}</strong></td>
                  <td>{pending.email}</td>
                  <td>
                    <span className="numeric">{pending.request_count}</span>
                    <small className="mt-1 block text-[var(--text-secondary)]">
                      {pending.request_numbers}
                    </small>
                  </td>
                  <td>
                    <PendingApprovalAction
                      pending={pending}
                      onAction={setMemberAction}
                    />
                  </td>
                </tr>
              ))}
            </Table>
          </div>
          <div
            className="grid gap-3 md:hidden"
            aria-label="Pending approval reassignments"
          >
            {data.pending.map((pending) => (
              <article
                key={pending.former_user_id}
                className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-sm)]"
              >
                <p className="eyebrow">Former approver</p>
                <h3 className="mt-1 text-base font-semibold">{pending.full_name}</h3>
                <p className="mt-1 break-all text-sm text-[var(--text-secondary)]">
                  {pending.email}
                </p>
                <dl className="mt-4 grid gap-1 text-sm">
                  <dt className="text-xs text-[var(--text-muted)]">Paused requests</dt>
                  <dd className="tabular-nums">
                    {pending.request_count} · {pending.request_numbers}
                  </dd>
                </dl>
                <div className="mt-4 border-t border-[var(--border)] pt-4 [&>button]:w-full">
                  <PendingApprovalAction
                    pending={pending}
                    onAction={setMemberAction}
                  />
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
      {rows.length ? (
        <>
          <div className="hidden md:block">
            <Table
              heads={[
                "Person",
                "Email",
                "Organisation role",
                "Access",
                "Invitation",
                "Added",
                "Actions",
              ]}
            >
              {rows.map((row) => {
                const self = row.user_id === currentUserId;
                return (
                  <tr key={row.user_id}>
                    <td>
                      <strong>{row.profiles?.full_name}</strong>
                      {self && (
                        <small className="mt-1 block text-[var(--text-secondary)]">
                          Signed-in account
                        </small>
                      )}
                    </td>
                    <td>{row.profiles?.email}</td>
                    <td>{titleCase(row.role)}</td>
                    <td><Badge value={row.status} /></td>
                    <td><InvitationState member={row} /></td>
                    <td>{displayDate(row.created_at)}</td>
                    <td>
                      <MemberActions
                        member={row}
                        self={self}
                        onAction={setMemberAction}
                      />
                    </td>
                  </tr>
                );
              })}
            </Table>
          </div>
          <div className="grid gap-3 md:hidden" aria-label="Organisation users">
            {rows.map((row) => {
              const self = row.user_id === currentUserId;
              return (
                <article
                  key={row.user_id}
                  className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-sm)]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="eyebrow">{titleCase(row.role)}</p>
                      <h2 className="mt-1 text-base font-semibold">
                        {row.profiles?.full_name}
                      </h2>
                      <p className="mt-1 break-all text-sm text-[var(--text-secondary)]">
                        {row.profiles?.email}
                      </p>
                    </div>
                    <Badge value={row.status} />
                  </div>
                  {self && (
                    <p className="mt-3 text-sm text-[var(--text-secondary)]">
                      Signed-in account
                    </p>
                  )}
                  <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <dt className="text-xs text-[var(--text-muted)]">Invitation</dt>
                      <dd className="mt-1"><InvitationState member={row} /></dd>
                    </div>
                    <div>
                      <dt className="text-xs text-[var(--text-muted)]">Added</dt>
                      <dd className="mt-1 tabular-nums">{displayDate(row.created_at)}</dd>
                    </div>
                  </dl>
                  <div className="mt-4 border-t border-[var(--border)] pt-4">
                    <MemberActions
                      member={row}
                      self={self}
                      onAction={setMemberAction}
                    />
                  </div>
                </article>
              );
            })}
          </div>
        </>
      ) : (
        <Empty
          title="No organisation users found"
          description="Invite role-specific users or clear the current search."
          action={
            <Button className="min-h-11" onClick={() => setInviting(true)}>
              Invite user
            </Button>
          }
        />
      )}
      <InviteDialog
        open={inviting}
        onOpenChange={setInviting}
        organisationId={organisation.id}
        onDone={changed}
      />
      {memberAction && (
        <MemberActionDialog
          key={`${memberAction.kind}-${
            "member" in memberAction
              ? memberAction.member.user_id
              : memberAction.pending.former_user_id
          }`}
          action={memberAction}
          members={data.members}
          organisationId={organisation.id}
          organisationName={organisation.name}
          onOpenChange={(open) => {
            if (!open) setMemberAction(undefined);
          }}
          onDone={changed}
        />
      )}
    </>
  );
}

function PendingApprovalAction({
  pending,
  onAction,
}: {
  pending: PendingApprovalReassignment;
  onAction: (action: MemberAction) => void;
}) {
  return (
    <Button
      className="min-h-11"
      variant="outline"
      onClick={() => onAction({ kind: "reassign_pending", pending })}
    >
      Reassign approvals
    </Button>
  );
}

function InvitationState({ member }: { member: TeamMember }) {
  return (
    <>
      <Badge value={member.invitation_state} />
      {member.invitation_state !== "accepted" && member.invitation_expires_at && (
        <small className="mt-1 block text-[var(--text-secondary)]">
          {member.invitation_state === "expired" ? "Expired" : "Expires"}{" "}
          {displayDate(member.invitation_expires_at)}
        </small>
      )}
    </>
  );
}

function MemberActions({
  member,
  self,
  onAction,
}: {
  member: TeamMember;
  self: boolean;
  onAction: (action: MemberAction) => void;
}) {
  if (self) {
    return (
      <span className="text-sm text-[var(--text-secondary)]">
        Self-service changes blocked
      </span>
    );
  }
  if (member.status === "invited") {
    return (
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap [&>button]:min-h-11 [&>button]:w-full sm:[&>button]:w-auto">
        <Button variant="outline" onClick={() => onAction({ kind: "resend", member })}>
          Resend
        </Button>
        <Button variant="destructive" onClick={() => onAction({ kind: "cancel", member })}>
          Cancel invitation
        </Button>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap [&>button]:min-h-11 [&>button]:w-full sm:[&>button]:w-auto">
      <Button variant="outline" onClick={() => onAction({ kind: "role", member })}>
        Change role
      </Button>
      <Button
        variant="outline"
        onClick={() =>
          onAction({
            kind: member.status === "suspended" ? "reactivate" : "suspend",
            member,
          })
        }
      >
        {member.status === "suspended" ? "Reactivate" : "Suspend"}
      </Button>
      <Button variant="destructive" onClick={() => onAction({ kind: "revoke", member })}>
        Revoke
      </Button>
    </div>
  );
}

function InviteDialog({
  open,
  onOpenChange,
  organisationId,
  onDone,
}: DialogProps) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await actions.inviteUser(organisationId, {
        fullName: String(form.get("name")),
        email: String(form.get("email")),
        role: String(form.get("role")),
        reason: String(form.get("reason")),
      });
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow="ACCESS CONTROL"
          title="Invite organisation user"
          description="Supabase sends a secure invitation email; no password is shared."
        />
        <form onSubmit={submit} className="grid gap-4 p-6">
          <div className="grid gap-4 md:grid-cols-2">
            <Label>
              Full name
              <Input name="name" required />
            </Label>
            <Label>
              Work email
              <Input name="email" type="email" required />
            </Label>
            <Label>
              Organisation role
              <Select name="role">
                {roles.map(([value, label]) => (
                  <option value={value} key={value}>{label}</option>
                ))}
              </Select>
            </Label>
          </div>
          <Label>
            Reason for invitation
            <Textarea name="reason" required />
          </Label>
          <FieldError>{error}</FieldError>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end [&>button]:w-full sm:[&>button]:w-auto">
            <Button
              className="min-h-11"
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button className="min-h-11" type="submit" disabled={busy}>
              Send invitation
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MemberActionDialog({
  action,
  members,
  organisationId,
  organisationName,
  onOpenChange,
  onDone,
}: {
  action: MemberAction;
  members: TeamMember[];
  organisationId: string;
  organisationName: string;
  onOpenChange: (open: boolean) => void;
  onDone: () => void;
}) {
  const member = "member" in action ? action.member : undefined;
  const pending = "pending" in action ? action.pending : undefined;
  const targetUserId = member ? member.user_id : pending!.former_user_id;
  const targetName = member ? member.profiles?.full_name || "Member" : pending!.full_name;
  const inFlightCount = member
    ? member.in_flight_approval_count
    : pending!.request_count;
  const [role, setRole] = useState(member?.role || "requester");
  const [approvalResolution, setApprovalResolution] = useState("");
  const [reassignTo, setReassignTo] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const eligibleReplacements = members.filter(
    (candidate) =>
      candidate.user_id !== targetUserId &&
      candidate.status === "active" &&
      ["approver", "tenant_admin"].includes(candidate.role),
  );
  const roleRemovesApproval =
    action.kind === "role" &&
    ["approver", "tenant_admin"].includes(member?.role || "") &&
    !["approver", "tenant_admin"].includes(role);
  const needsApprovalResolution =
    inFlightCount > 0 &&
    (["suspend", "revoke"].includes(action.kind) || roleRemovesApproval);
  const needsReplacement =
    action.kind === "reassign_pending" ||
    (needsApprovalResolution && approvalResolution === "reassign");
  const destructive = ["suspend", "revoke", "cancel"].includes(action.kind);
  const titles: Record<MemberAction["kind"], string> = {
    role: "Change organisation role",
    suspend: "Suspend member",
    reactivate: "Reactivate member",
    revoke: "Revoke membership",
    resend: "Resend invitation",
    cancel: "Cancel invitation",
    reassign_pending: "Reassign pending approvals",
  };
  const consequences: Record<MemberAction["kind"], string> = {
    role: `${targetName}'s permissions will change immediately.`,
    suspend: `${targetName} will immediately lose access to ${organisationName} and be excluded from approval routing. If this is their only active RAQOZ access, sign-in will also be blocked.`,
    reactivate: `${targetName} will regain access to ${organisationName} under their current role.`,
    revoke: `${targetName}'s membership of ${organisationName} will be removed entirely. This does not delete the audit record.`,
    resend: `A fresh invitation will be sent to ${targetName} and the seven-day expiry will restart.`,
    cancel: `${targetName}'s pending invitation to ${organisationName} will be cancelled and they will not receive organisation access.`,
    reassign_pending: `${inFlightCount} submitted requisition${inFlightCount === 1 ? "" : "s"} formerly assigned to ${targetName} will move to the selected active approver.`,
  };

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const reason = String(form.get("reason"));
    setBusy(true);
    setError("");
    try {
      if (action.kind === "role") {
        await actions.changeMemberRole(
          organisationId,
          targetUserId,
          role,
          reason,
          needsApprovalResolution ? approvalResolution : undefined,
          needsReplacement ? reassignTo : undefined,
        );
      } else if (action.kind === "suspend" || action.kind === "reactivate") {
        await actions.setMemberStatus(
          organisationId,
          targetUserId,
          action.kind === "suspend" ? "suspended" : "active",
          reason,
          needsApprovalResolution ? approvalResolution : undefined,
          needsReplacement ? reassignTo : undefined,
        );
      } else if (action.kind === "revoke") {
        await actions.revokeMember(
          organisationId,
          targetUserId,
          reason,
          needsApprovalResolution ? approvalResolution : undefined,
          needsReplacement ? reassignTo : undefined,
        );
      } else if (action.kind === "resend") {
        await actions.resendUserInvitation(organisationId, targetUserId, reason);
      } else if (action.kind === "cancel") {
        await actions.cancelUserInvitation(organisationId, targetUserId, reason);
      } else {
        await actions.reassignPendingMemberApprovals(
          organisationId,
          targetUserId,
          reassignTo,
          reason,
        );
      }
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow="ACCESS CONTROL"
          title={titles[action.kind]}
          description={consequences[action.kind]}
        />
        <form onSubmit={submit} className="grid gap-4 p-6">
          {action.kind === "role" && (
            <Label>
              New organisation role
              <Select value={role} onChange={(event) => setRole(event.target.value as TeamMember["role"])}>
                {roles.map(([value, label]) => (
                  <option value={value} key={value}>{label}</option>
                ))}
              </Select>
            </Label>
          )}
          {needsApprovalResolution && (
            <Label>
              {inFlightCount} in-flight approval assignment{inFlightCount === 1 ? "" : "s"}
              <Select
                required
                value={approvalResolution}
                onChange={(event) => setApprovalResolution(event.target.value)}
              >
                <option value="">Choose what happens</option>
                <option value="reassign">Reassign now</option>
                <option value="leave_for_reassignment">Leave marked for reassignment</option>
              </Select>
            </Label>
          )}
          {needsReplacement && (
            <Label>
              Replacement approver
              <Select
                required
                value={reassignTo}
                onChange={(event) => setReassignTo(event.target.value)}
              >
                <option value="">Select active approver</option>
                {eligibleReplacements.map((candidate) => (
                  <option value={candidate.user_id} key={candidate.user_id}>
                    {candidate.profiles?.full_name} · {titleCase(candidate.role)}
                  </option>
                ))}
              </Select>
            </Label>
          )}
          <Label>
            Reason
            <Textarea name="reason" required />
          </Label>
          <FieldError>{error}</FieldError>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end [&>button]:w-full sm:[&>button]:w-auto">
            <Button
              className="min-h-11"
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Keep current access
            </Button>
            <Button
              className="min-h-11"
              type="submit"
              variant={destructive ? "destructive" : "default"}
              disabled={busy || (needsReplacement && !eligibleReplacements.length)}
            >
              {titles[action.kind]}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
